// outlinerTree — the docked shell's outliner rows, derived from what is actually
// in the scene.
//
// Every row is REAL: counts come from the live THREE scene graph and the live
// graph-state modules, never from placeholder fixtures. A thing that isn't
// present contributes no row (the empty outliner is the no-character state), and
// a count we can't compute is omitted from the row rather than invented.
//
// Two kinds of row, distinguished by `adapterId`:
//   - adapter rows (Avatar / Environment / Torch) — selecting one arms the
//     transform gizmo via sceneSelection, exactly as the old OutlinerRail did.
//   - info rows (Armature / meshes / Face rig / Event graph) — selecting one
//     only feeds the inspector. They exist because "what is this character made
//     of" is the outliner's job even where no gizmo applies.
//
// Pure function over injected inputs so `node --test` can drive it with a fake
// scene — no window, no THREE import (it reads structural booleans THREE
// already stamps on objects: isBone, isMesh, isSkinnedMesh).

/**
 * Full-viewport editor surface id → human label.
 *
 * Exported rather than inlined at the one call site below because the browser tab
 * title names the open surface too (src/lib/documentTitle.js). Two copies of these
 * three strings is two places to rename and one of them to forget.
 */
export const SURFACE_LABELS = Object.freeze({
  body: 'Body graph',
  face: 'Face graph',
  events: 'Event graph',
})

/**
 * Glyphs for the same three surfaces — tool rail, menu items and outliner rows.
 *
 * Every one is a TEXT-presentation character, and that is the whole constraint.
 * The Event Graph used to be '⚡' (U+26A1), which carries default EMOJI
 * presentation: browsers render it from a colour emoji font, so it ignored the
 * rail's `color` entirely — permanently orange, unaffected by hover or by the
 * active state that tints every other glyph. It read as a sticker dropped onto a
 * row of line icons because that is effectively what it was. '↯' is the same
 * meaning as a glyph the font actually draws.
 *
 * Face was '☺' (U+263A), a smiley — legible, but a cartoon among geometric marks.
 * '◎' reads as an iris and sits in the same family as the body hexagon.
 *
 * Kept deliberately plain and widely-covered (DejaVu, Segoe UI Symbol, Menlo): an
 * obscure APL or Miscellaneous-Technical glyph would be prettier on one machine
 * and a tofu box on the next.
 */
export const SURFACE_GLYPH = Object.freeze({
  body: '⬡',
  face: '◎',
  events: '↯',
})

/** Inspector type-badge colors ride the wire tokens, same convention as the
 *  launcher's pipeline badges: color encodes category, not theme. */
import { navmeshRowStatus, artifactRowStatus } from './navmeshStatus.js'

export const TYPE_TOKEN = {
  CHARACTER: '--m-accent',
  SKELETON: '--m-wire-anim',
  MESH: '--m-wire-state',
  FACE: '--m-wire-state',
  GRAPH: '--m-wire-event',
  GSPLAT: '--m-wire-output',
  LIGHT: '--m-wire-output',
  NAVMESH: '--m-wire-anim',
  EMPTY: '--m-fg-3',
}

function countBones(scene) {
  let n = 0
  scene?.traverse?.(o => { if (o.isBone) n += 1 })
  return n
}

function collectMeshes(scene) {
  const out = []
  scene?.traverse?.(o => {
    if (!o.isMesh && !o.isSkinnedMesh) return
    const index = o.geometry?.index
    const pos = o.geometry?.attributes?.position
    const tris = index ? Math.floor(index.count / 3) : pos ? Math.floor(pos.count / 3) : null
    const morphs = o.morphTargetInfluences?.length ?? 0
    out.push({ name: o.name || '(unnamed mesh)', tris, morphs, visible: o.visible !== false })
  })
  return out
}

const fmt = n => (Number.isFinite(n) ? n.toLocaleString('en-US') : null)

/**
 * @param {object} input
 * @param {{id?:string,label?:string}|null} input.character
 * @param {Array<{id:string,label:string,isAvailable:()=>boolean,note?:string}>} input.adapters
 *   live transform adapters (getAdapters()).
 * @param {object|null} input.scene        THREE scene root (window.__AVATAR_SCENE__).
 * @param {'rig'|'splat'|'stream'|'missing'|'unknown'|null} input.rigStatus
 * @param {{nodes?:Array}|null} input.bodyGraph    graphState module ref
 * @param {{nodes?:Array}|null} input.faceGraph    faceGraphState module ref
 * @param {{nodes?:Array,edges?:Array}|null} input.eventGraph  eventGraphState module ref
 * @param {string|null} input.environmentName  active splat's display name
 * @param {boolean|null} [input.environmentBakeable]  isBakeableUrl(splatUrl) —
 *   whether the active environment's file is a .ply the bake service can
 *   rasterize. `false` disables the Build Navmesh button with the reason;
 *   null/undefined (unknown) leaves it enabled, so a caller that doesn't pass
 *   it keeps today's behaviour.
 * @param {'rig'|'editor'|null} [input.cameraOwner]  who drives the camera right
 *   now: the blueprint's authored rig, or the editor camera fallback. null when
 *   there is no scene to have a camera in.
 * @param {{location?:number[],rotation?:number[],target?:number[],fov?:number}|null}
 *   [input.cameraTransform]  cameraController.getTransform(), or null when the
 *   controller isn't bound to a camera yet.
 * @param {number|null} [input.cameraFov]  liveTuneStore's configured fov.
 * @param {string|null} [input.cameraTargetMode]  'fixed' | 'head' | 'vector'.
 * @param {'clean'|'dirty'|'saving'|'error'|'stale-backend'} [input.dirtyState]
 * @param {{dim?:number,subject?:string|null}|null} [input.rigBoard]  the loaded
 *   OGS bundle's own control space (rigBoardSource) — the SPLAT character's face
 *   rig, since it has no morph-target mesh to read one off. Passed in as a module
 *   ref, same as the graph states, so this stays pure for `node --test`.
 * @param {{available?:boolean,blend?:number,initial?:number,source?:string}|null}
 *   [input.rigConstrain]  the live ?rigconstrain weight (ogs/rig/rigConstrainBlend's
 *   module singleton) — how much of the trained clamp + subspace projection reaches
 *   the splat face rig. Drives the Face rig row's CONSTRAINT slider. Passed in, not
 *   read, so this stays pure for `node --test`.
 * @param {{available?:boolean,mouthWeight?:number,initial?:number,source?:string}|null}
 *   [input.liveFace]  the live-speech drive weights (ogs/rig/liveFaceControls) — the
 *   Face rig row's mouth-mix slider. Passed in, not read, so this stays pure.
 * @param {{state?:string,nodeCount?:number,size?:number}|null} [input.navmesh]
 *   window.__NAVMESH__ verbatim — the navigation row's dot + meta come from
 *   navmeshStatus.js's rules, not from this module's own reading of it. Passed in
 *   (not read here) so the function stays pure for `node --test`.
 * @param {{kind?:string,pct?:number}|null} [input.navmeshBake]  the navmesh row's
 *   bake state, so a running build reads as building rather than as missing.
 * @param {{navmesh?:boolean,voxels?:boolean,collision?:boolean,capsule?:boolean,
 *   walkPath?:boolean}|null} [input.navDebug]  navSettingsStore's `show` map —
 *   which navigation debug layers are drawn. Passed in, not read, so this stays
 *   pure for `node --test`.
 * @param {{agentRadius?:number,agentHeight?:number,navCell?:number}|null}
 *   [input.navParams]  the navmesh build parameters (the retired Build Navmesh
 *   panel's fields, now the NAVMESH row's own properties).
 * @param {{collisionMode?:string,collisionOnTop?:boolean}|null} [input.navStyle]
 *   how the collision mesh is drawn — the COLLISION row's properties.
 * @param {{state?:string,count?:number}|null} [input.voxels]  window.__VOXELS__.
 * @param {{state?:string}|null} [input.collision]  window.__COLLISION__.
 * @param {{groundEnabled?:boolean,groundColor?:string,groundSize?:number}|null}
 *   [input.backdrop]  liveTuneStore.getState().backdrop — drives the ground
 *   plane row's eye toggle. Passed in (not read here) so the function stays
 *   pure for `node --test`.
 * @returns {Array<{id:string,label:string,glyph:string,depth:number,meta:string|null,
 *                  type:string,adapterId:string|null,parentId:string|null,
 *                  openSurface?:string,visible?:boolean,
 *                  status?:{tone:'ok'|'warn'|'error',title:string},
 *                  controls?:Array<{kind:'toggle'|'number'|'slider'|'choice'|'button'|'group',
 *                    id?:string,label:string,value?:any,enabled?:boolean,title?:string}>,
 *                  kv:Array<[string,string]>}>}
 */
export function buildOutlinerRows({
  character = null,
  adapters = [],
  scene = null,
  rigStatus = null,
  bodyGraph = null,
  faceGraph = null,
  eventGraph = null,
  environmentName = null,
  environmentBakeable = null,
  dirtyState = 'clean',
  cameraOwner = null,
  cameraTransform = null,
  cameraFov = null,
  navmesh = null,
  navmeshBake = null,
  navDebug = null,
  navParams = null,
  navStyle = null,
  voxels = null,
  collision = null,
  cameraTargetMode = null,
  backdrop = null,
  rigBoard = null,
  rigConstrain = null,
  liveFace = null,
} = {}) {
  if (!character) return []

  const rows = []
  const byLabel = (label) => adapters.find(a => a.label === label && a.isAvailable()) ?? null

  // ── Character root — the avatar adapter when one is live ──────────────────
  const avatarAdapter = byLabel('Avatar')
  const bodyNodes = bodyGraph?.nodes?.length ?? 0
  const faceNodes = faceGraph?.nodes?.length ?? 0
  const eventNodes = eventGraph?.nodes?.length ?? 0
  const blueprints = [bodyNodes && 'body', faceNodes && 'face', eventNodes && 'events']
    .filter(Boolean).join(' · ')

  rows.push({
    id: 'character',
    label: character.label ?? character.id ?? 'Character',
    glyph: '◈',
    depth: 0,
    meta: 'CHARACTER',
    type: 'CHARACTER',
    adapterId: avatarAdapter?.id ?? null,
    parentId: null,
    // NO rigPreview here. The rig board belongs to the Face rig row, which is
    // where you go looking for face controls and which now exists for every
    // splat character (see the Face rig section below) — so the root's copy was
    // a second button for the same panel one row above the row that owns it.
    // It was on the root originally (ENG-946) only because the Face rig row was
    // read off morph-target meshes a splat character hasn't got. (ENG-1042)
    kv: [
      ['id', character.id ?? '—'],
      ...(rigStatus && rigStatus !== 'unknown' ? [['pipeline', rigStatus.toUpperCase()]] : []),
      ...(blueprints ? [['blueprints', blueprints]] : []),
      ...(environmentName ? [['environment', environmentName]] : []),
      ['unsaved', dirtyState === 'clean' ? 'no' : 'yes'],
    ],
  })

  // ── Skeleton ───────────────────────────────────────────────────────────────
  const bones = countBones(scene)
  if (bones > 0) {
    rows.push({
      id: 'armature',
      label: 'Armature',
      glyph: '⌒',
      depth: 1,
      meta: String(bones),
      type: 'SKELETON',
      adapterId: null,
      parentId: 'character',
      kv: [['joints', fmt(bones)]],
    })
  }

  // ── Meshes ─────────────────────────────────────────────────────────────────
  const meshes = scene ? collectMeshes(scene) : []
  if (meshes.length) {
    const totalTris = meshes.reduce((s, m) => s + (m.tris ?? 0), 0)
    rows.push({
      id: 'meshes',
      label: 'Meshes',
      glyph: '▤',
      depth: 1,
      meta: String(meshes.length),
      type: 'GROUP',
      adapterId: null,
      parentId: 'character',
      kv: [
        ['children', String(meshes.length)],
        ...(totalTris ? [['tris', fmt(totalTris)]] : []),
      ],
    })
    for (const m of meshes) {
      rows.push({
        id: `mesh:${m.name}`,
        label: m.name,
        glyph: '◐',
        depth: 2,
        meta: 'MESH',
        type: 'MESH',
        adapterId: null,
        parentId: 'meshes',
        kv: [
          ...(m.tris != null ? [['tris', fmt(m.tris)]] : []),
          ...(m.morphs ? [['morph targets', String(m.morphs)]] : []),
          ['visible', m.visible ? 'yes' : 'no'],
        ],
      })
    }
  }

  // ── Face rig ───────────────────────────────────────────────────────────────
  // TWO sources, and which one is real is decided by the pipeline — this row is
  // where you go looking for face controls, so it must exist for both (ENG-1042).
  //
  //   GLB character  — the face IS the head mesh's morph targets. Only claimed
  //                    when some mesh actually carries them.
  //   SPLAT character — there is no such mesh: `scene` here is
  //                    window.__AVATAR_SCENE__, which ONLY AvatarModel sets and
  //                    which never mounts in splat mode. Reading the row off
  //                    morph targets is why a splat character had no Face rig row
  //                    at all. Its face is the bundle's own control space
  //                    (rigBoardSource), so that is what the row reports.
  const morphed = meshes.filter(m => m.morphs > 0)
  const boardDim = Number(rigBoard?.dim) || 0
  // The face rig's own PROPERTIES, in the inspector rather than in a floating panel,
  // for the same reason the navmesh build params belong to the NAVMESH row: a
  // feature's properties live with the feature, and this is the row you already open
  // to ask what the face rig is.
  //
  // Two groups, and the split is the point — they are different jobs on the same
  // frame. `Live speech` is a DRIVE (the trained mouth mix rewrites the mouth block);
  // `Constraint` BOUNDS whatever wrote it (the trained-domain clamp + the subspace
  // projection). They sit together because they interact: at the shipped 0.65 the mix
  // has already pulled the mouth most of the way onto the trained manifold before
  // either constraint runs, which is why the constraint moves a talking face far less
  // than a static extreme pose.
  //
  // Each half is offered on its OWN terms — a bundle can ship a mouth drive with no
  // armed constraint weight, or the reverse — and neither appears on a GLB character,
  // where none of this path runs. A slider that silently does nothing is worse than
  // no slider. Each reset appears only once the value differs from what this
  // character loaded with, since that is the only state it has a job in.
  const mouthControls = () => {
    if (!liveFace?.available) return []
    const m = Number(liveFace.mouthWeight ?? 0.65)
    const init = Number(liveFace.initial ?? 0.65)
    return [
      { kind: 'group', label: 'Live speech' },
      {
        kind: 'slider', id: 'live-mouth', label: 'trained mouth mix',
        value: m, min: 0, max: 1, step: 0.05, format: (v) => v.toFixed(2),
        title: "How far the live A2F mouth block is pulled toward this bundle's trained "
          + 'closed/open references. The A2F jaw still picks the aperture; the references '
          + 'decide what a mouth at that aperture looks like. 0 = the raw rest+delta mouth.',
        note: m <= 0 ? 'OFF — the raw A2F mouth (rest + delta), where the live lip artifacts come from.'
          : m >= 1 ? 'The mouth block is REPLACED by the trained interpolation; A2F sets only the aperture.'
            : `${Math.round(m * 100)}% toward the trained references; A2F keeps the aperture.`,
      },
      ...(m !== init ? [{
        kind: 'button', id: 'live-mouth-reset', label: `reset to ${init.toFixed(2)}`,
        title: liveFace.source === 'panel'
          ? 'Back to the value this character loaded with'
          : 'Back to the URL / default value',
      }] : []),
    ]
  }
  const constraintControls = () => {
    if (!rigConstrain?.available) return []
    const w = Number(rigConstrain.blend ?? 1)
    const initial = Number(rigConstrain.initial ?? 1)
    return [
      { kind: 'group', label: 'Constraint' },
      {
        kind: 'slider', id: 'rig-constrain', label: 'trained clamp + PCA',
        value: w, min: 0, max: 1, step: 0.05, format: (v) => v.toFixed(2),
        title: 'How much of the trained-domain clamp and the trained subspace projection reaches '
          + 'the rig. 1 = the shipped face; 0 = the raw ARKit-composed rig (?rigclamp=none&rigpca=off).',
        // What it is doing RIGHT NOW, when there is a twin to compare against.
        // "The slider does nothing" and "this pose cannot show what it is doing" look
        // identical on screen and have opposite fixes.
        live: rigConstrain.delta
          ? `now moving ${rigConstrain.delta.moved} controls, worst ${rigConstrain.delta.max.toFixed(3)}`
          : null,
        note: w >= 1 ? 'Trained clamp + subspace projection fully applied — the shipped face.'
          : w <= 0 ? 'NEITHER constraint — the raw ARKit rig. Expect the decoders to over-respond.'
            : `${Math.round(w * 100)}% of the constraint — blended between the constrained rig and the raw one.`,
      },
      ...(w !== initial ? [{
        kind: 'button', id: 'rig-constrain-reset', label: `reset to ${initial.toFixed(2)}`,
        title: rigConstrain.source === 'panel'
          ? 'Back to the value this character loaded with'
          : 'Back to the URL / default value',
      }] : []),
    ]
  }
  const faceRigControls = () => {
    const all = [...mouthControls(), ...constraintControls()]
    return all.length ? all : undefined
  }

  if (morphed.length) {
    rows.push({
      id: 'face-rig',
      label: 'Face rig',
      glyph: SURFACE_GLYPH.face,
      depth: 1,
      meta: String(Math.max(...morphed.map(m => m.morphs))),
      type: 'FACE',
      adapterId: null,
      parentId: 'character',
      rigPreview: rigStatus === 'splat',
      controls: faceRigControls(),
      kv: [
        ['shape targets', String(Math.max(...morphed.map(m => m.morphs)))],
        ['carried by', morphed.map(m => m.name).join(', ')],
        ...(faceNodes ? [['face graph nodes', String(faceNodes)]] : []),
      ],
    })
  } else if (rigStatus === 'splat') {
    // The row exists on the strength of the PIPELINE, not the board: the panel
    // always offers ARKit-52 for a splat character, so the controls are reachable
    // before the bundle publishes its own control space. The board's numbers fill
    // in when it registers — an unpublished board contributes no count rather
    // than a plausible one.
    rows.push({
      id: 'face-rig',
      label: 'Face rig',
      glyph: SURFACE_GLYPH.face,
      depth: 1,
      meta: boardDim ? String(boardDim) : null,
      type: 'FACE',
      adapterId: null,
      parentId: 'character',
      rigPreview: true,
      controls: faceRigControls(),
      kv: [
        ...(boardDim ? [['rig controls', String(boardDim)]] : []),
        ...(rigBoard?.subject ? [['subject', String(rigBoard.subject)]] : []),
        ...(faceNodes ? [['face graph nodes', String(faceNodes)]] : []),
      ],
    })
  }

  // ── Blueprints — the three graphs, each row opening its full-viewport
  //    editor (double-click, or the inspector's Open button). Counts are the
  //    live module refs, so an empty graph reads 0 rather than pretending. ──
  const graphRows = [
    { id: 'graph-body', label: SURFACE_LABELS.body, surface: 'body', count: bodyNodes, extra: bodyGraph },
    { id: 'graph-face', label: SURFACE_LABELS.face, surface: 'face', count: faceNodes, extra: faceGraph },
    { id: 'graph-events', label: SURFACE_LABELS.events, surface: 'events', count: eventNodes, extra: eventGraph },
  ]
  rows.push({
    id: 'blueprints',
    label: 'Blueprints',
    glyph: SURFACE_GLYPH.events,
    depth: 1,
    meta: String(graphRows.filter(g => g.count > 0).length),
    type: 'GRAPH',
    adapterId: null,
    parentId: 'character',
    kv: graphRows.map(g => [g.label.toLowerCase(), `${g.count} nodes`]),
  })
  for (const g of graphRows) {
    rows.push({
      id: g.id,
      label: g.label,
      glyph: '⌁',
      depth: 2,
      meta: String(g.count),
      type: 'GRAPH',
      adapterId: null,
      parentId: 'blueprints',
      openSurface: g.surface,
      kv: [
        ['nodes', String(g.count)],
        ...(g.extra?.edges ? [['edges', String(g.extra.edges.length)]] : []),
      ],
    })
  }

  // ── Camera ─────────────────────────────────────────────────────────────────
  // The camera has no transform adapter because it is not a scene object you
  // drag: in live mode the BLUEPRINT drives it (an authored rig — the
  // `cam_group_orbit` graphGroup in the Event Graph), and with no rig authored
  // the editor's OrbitControls takes over as a LABELLED fallback (Scene.jsx,
  // cameraRigCompat). That distinction is invisible everywhere else in the
  // shell, which is exactly why it earns a row: the row's meta IS the answer to
  // "who is moving my camera".
  //
  // Double-click opens the Event Graph, because that is where a rig is authored
  // — whether you are inspecting the one you have or going to add one.
  if (cameraOwner) {
    const rigDriven = cameraOwner === 'rig'
    const fov = Number.isFinite(cameraTransform?.fov) ? cameraTransform.fov
      : Number.isFinite(cameraFov) ? cameraFov : null
    rows.push({
      id: 'camera',
      label: 'Camera',
      glyph: '▣',
      depth: 0,
      meta: rigDriven ? 'RIG' : 'EDITOR',
      type: 'CAMERA',
      adapterId: null,
      parentId: null,
      openSurface: 'events',
      openLabel: 'Open Event graph',
      kv: [
        ['driven by', rigDriven ? 'blueprint rig' : 'editor camera'],
        // The diagnostic the notice chip carries, restated where you go looking
        // for the camera rather than only where it happens to overlay.
        ...(rigDriven ? [] : [['rig', 'none authored — add one in the Event Graph']]),
        ...(fov != null ? [['fov', `${Math.round(fov)}°`]] : []),
        ...(cameraTargetMode ? [['target', cameraTargetMode]] : []),
        // Position/rotation only when the controller is actually bound to a
        // camera; an unbound controller reports nothing rather than zeros.
        ...(cameraTransform?.location
          ? [['location', cameraTransform.location.map(v => v.toFixed(2)).join('  ')]] : []),
        ...(cameraTransform?.target
          ? [['look at', cameraTransform.target.map(v => v.toFixed(2)).join('  ')]] : []),
      ],
    })
  }

  // ── Environment ────────────────────────────────────────────────────────────
  const envAdapter = byLabel('Environment')
  if (envAdapter) {
    rows.push({
      id: 'environment',
      label: environmentName ?? 'Environment',
      glyph: '⬢',
      depth: 0,
      meta: 'GSPLAT',
      type: 'GSPLAT',
      adapterId: envAdapter.id,
      parentId: null,
      kv: [
        ...(environmentName ? [['source', environmentName]] : []),
        ...(envAdapter.note ? [['note', envAdapter.note]] : []),
      ],
    })
  }

  // ── Torch (ENG-895 movable light) ─────────────────────────────────────────
  const torchAdapter = byLabel('Torch')
  if (torchAdapter) {
    rows.push({
      id: 'torch',
      label: 'Torch',
      glyph: '☀',
      depth: 0,
      meta: 'LIGHT',
      type: 'LIGHT',
      adapterId: torchAdapter.id,
      parentId: null,
      kv: torchAdapter.note ? [['note', torchAdapter.note]] : [],
    })
  }

  // ── Navmesh (navigation) ───────────────────────────────────────────────────
  // Like the ground plane, NOT a scene object: it's the navigation graph the
  // runtime paths on (window.__NAVMESH__, published by NavMeshVolume). It earns a
  // row because "can the character walk here?" is an outliner question, and
  // because the answer was previously invisible — a missing bake surfaced only as
  // a console warning, so a right-click "Walk here" that refused had nothing to
  // point at. The dot is the answer (green = navigable now), the meta says which
  // KIND (baked / studio floor), and selecting the row draws the graph.
  const nmStatus = navmeshRowStatus(navmesh, navmeshBake)
  // The sibling artifacts' status feeds the enabled/reason of their controls
  // below — they have no row of their own any more.
  const vxStatus = artifactRowStatus(voxels, 'Voxels', 'voxel grid')
  const clStatus = artifactRowStatus(collision, 'Collision', 'collision mesh')
  rows.push({
    id: 'navmesh',
    label: nmStatus.label,
    glyph: '⬡',
    depth: 0,
    meta: nmStatus.meta,
    type: 'NAVMESH',
    adapterId: null,
    parentId: null,
    status: { tone: nmStatus.tone, title: nmStatus.detail },
    // The navmesh's own properties — what the retired "Build Navmesh" panel
    // held. An unavailable control is disabled WITH its reason, never hidden.
    controls: [
      {
        kind: 'toggle', id: 'show-navmesh', label: 'Show navmesh grid',
        value: !!navDebug?.navmesh, enabled: nmStatus.built,
        title: nmStatus.built
          ? 'Draw the walkable graph in the viewport'
          : 'Nothing to draw yet — ' + nmStatus.detail,
      },
      {
        kind: 'toggle', id: 'show-capsule', label: 'Show agent capsule',
        value: !!navDebug?.capsule, enabled: true,
        title: 'The character capsule the build carves around (radius + height below)',
      },
      {
        // The right-click "Walk here" preview: route line + destination ring.
        kind: 'toggle', id: 'show-walk-path', label: 'Show walk path',
        value: navDebug?.walkPath !== false, enabled: true,
        title: 'Draw the planned route and destination when walking somewhere',
      },
      { kind: 'group', label: 'Character capsule' },
      {
        kind: 'number', id: 'agent-radius', label: 'radius', unit: 'm',
        value: navParams?.agentRadius ?? 0.25, step: 0.05, min: 0.05,
        title: 'Walkable erosion — how close to a wall the character may stand',
      },
      {
        kind: 'number', id: 'agent-height', label: 'height', unit: 'm',
        value: navParams?.agentHeight ?? 1.7, step: 0.1, min: 0.5,
        title: 'Standing clearance — how low a ceiling still counts as walkable',
      },
      {
        kind: 'number', id: 'nav-cell', label: 'cell', unit: 'm',
        value: navParams?.navCell ?? 0.2, step: 0.05, min: 0.05,
        title: 'Navmesh grid resolution',
      },
      {
        // Building needs an environment to bake FOR: the studio floor grid is
        // synthesised on the client, so there is nothing to send. And it needs
        // a .ply SOURCE: the bake rasterizer is INRIA .ply-only. A compressed
        // row (.spz) whose folder retains the source .ply (splat_ply, ENG-1070)
        // is still bakeable — the server bakes the sibling — so `false` here
        // means the server lists NO .ply at all, and the button disables with
        // that reason instead of relaying the bake's mid-parse 500.
        kind: 'button', id: 'build-navmesh', label: 'Build Navmesh',
        enabled: !!environmentName && environmentBakeable !== false, primary: true,
        title: !environmentName
          ? 'Load an environment first — the studio floor grid needs no bake'
          : environmentBakeable === false
            ? 'This environment has no .ply source — the bake can only rasterize .ply, and the server lists no .ply for it. Upload the .ply to build a navmesh.'
            : 'Bake the navmesh for ' + environmentName,
      },
      // The navmesh bake's SIBLING artifacts. They had their own outliner rows
      // briefly and Andres cut them: two more permanent rows for layers people
      // flip while debugging a bake is clutter in a list you read constantly.
      // Their controls stay — they are the only UI for these layers now that the
      // Build Navmesh window is gone — and they live here because they come from
      // the same bake. Each is disabled with its own reason when its artifact
      // isn't there, which is what the rows were carrying.
      ...(environmentName ? [
        { kind: 'group', label: 'Voxels' },
        {
          kind: 'toggle', id: 'show-voxels', label: 'Show voxels',
          value: !!navDebug?.voxels, enabled: vxStatus.built,
          title: vxStatus.built ? 'Draw the voxel-surface cubes' : 'Nothing to draw yet — ' + vxStatus.detail,
        },
        { kind: 'group', label: 'Collision mesh' },
        {
          kind: 'toggle', id: 'show-collision', label: 'Show collision mesh',
          value: !!navDebug?.collision, enabled: clStatus.built,
          title: clStatus.built ? 'Draw the baked collision surface' : 'Nothing to draw yet — ' + clStatus.detail,
        },
        {
          kind: 'choice', id: 'collision-mode', label: 'style',
          value: navStyle?.collisionMode ?? 'shaded',
          options: [['shaded', 'shaded'], ['wireframe', 'wireframe']],
          enabled: clStatus.built,
          title: 'Shaded reads the near surface; wireframe reads the edges',
        },
        {
          kind: 'toggle', id: 'collision-on-top', label: 'Always on top',
          value: !!navStyle?.collisionOnTop, enabled: clStatus.built,
          title: 'Ignore depth so the splat cannot occlude it',
        },
      ] : []),
    ],
    kv: [
      ['built', nmStatus.built ? 'yes' : 'no'],
      ...(Number.isFinite(navmesh?.nodeCount) ? [['nodes', String(navmesh.nodeCount)]] : []),
      ...(Number.isFinite(navmesh?.size) ? [['size', `${navmesh.size} m`]] : []),
      ...(Number.isFinite(navmesh?.floorY) ? [['floor y', String(navmesh.floorY)]] : []),
    ],
  })

  // ── Ground plane (backdrop) ────────────────────────────────────────────────
  // NOT a scene object: it's liveTuneStore.backdrop.groundEnabled, rendered
  // conditionally by Scene.jsx. It still earns a row because "why is there a
  // floor / why is there NO floor" is an outliner question — answered with an
  // eye toggle (`visible`, the store value verbatim) instead of a gizmo. It
  // rides along with any character, since the stage exists whether or not the
  // plane is currently shown.
  rows.push({
    id: 'groundPlane',
    label: 'Ground Plane',
    glyph: '▬',
    depth: 0,
    meta: 'PLANE',
    type: 'PLANE',
    adapterId: null,
    parentId: null,
    visible: !!backdrop?.groundEnabled,
    kv: [
      ['visible', backdrop?.groundEnabled ? 'yes' : 'no'],
      ...(backdrop?.groundColor ? [['color', String(backdrop.groundColor)]] : []),
      ...(Number.isFinite(backdrop?.groundSize) ? [['size', `${backdrop.groundSize} m`]] : []),
    ],
  })

  return rows
}

export default buildOutlinerRows
